import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CircleCheck,
  FileText,
  PackageCheck,
  PackageMinus,
  Send,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  fetchPendingDispatch,
  moveOrderToCompleted,
  removeAssignedWarehouse,
} from "../../../store/slices/orderSlice";
import Button, { ButtonType } from "../../../components/Assets/Button";
import PaginationControls from "../../../utils/PaginationControls";
import ChallanPreviewModal from "./ChallanPreviewModal";
import ConfirmationModal from "../../../components/Assets/ConfirmationModal";
import toast from "react-hot-toast";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";

interface PendingDispatchTabProps {
  onDispatchClick?: (orderId: string) => void;
  searchTerm?: string;
  dateRange?: { from: string; to: string };
}

const PendingDispatchTab: React.FC<PendingDispatchTabProps> = ({
  onDispatchClick,
  searchTerm,
  dateRange = { from: "", to: "" },
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { pendingDispatch, paginations, loading } = useSelector(
    (state: RootState) => state.order,
  );
  const role = useSelector((state: RootState) => state.auth.role);
  const [page, setPage] = useState(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isConfirmationModalDispatchOpen, setIsConfirmationModalDispatchOpen] =
    useState(false);
  const [visibleProductsMap, setVisibleProductsMap] = useState<
    Record<string, number>
  >({});
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [challanModalOpen, setChallanModalOpen] = useState(false);

  const fetchPendingDispatchCallback = useCallback(async () => {
    await dispatch(
      fetchPendingDispatch({
        page,
        search: searchTerm,
        startDate: dateRange.from,
        endDate: dateRange.to,
      }),
    );
  }, [dispatch, page, searchTerm, dateRange]);

  useEffect(() => {
    fetchPendingDispatchCallback();
  }, [fetchPendingDispatchCallback]);

  const pagination = paginations.pendingDispatch;

  return (
    <section>
      <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center">
        <span className="w-1 h-6 bg-yellow-500 mr-3 rounded-full" />
        Pending Dispatch
        <span className="ml-2 bg-yellow-100 text-yellow-800 text-sm px-2.5 py-0.5 rounded-full">
          {pagination?.total || 0}
        </span>
      </h2>

      {pendingDispatch.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No orders pending dispatch
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {pendingDispatch.map((order: any) => {
            const totalDispatched =
              order.dispatchQuantity?.reduce(
                (s: number, dq: any) => s + (dq.quantity || 0),
                0,
              ) ?? 0;

            const limit = visibleProductsMap[order._id] || 5;
            const items = order.goods || [];

            return (
              <motion.div
                key={order._id}
                layout
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="break-inside-avoid mb-5 bg-white rounded-2xl p-6 shadow-xs border border-gray-100 hover:shadow-md transition-shadow cursor-pointer relative overflow-hidden flex flex-col min-h-[460px]"
                whileHover={{ y: -4 }}
                onClick={() => onDispatchClick?.(order._id)}
              >
                <div className="flex justify-between items-start gap-2 mb-3">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <div className="bg-yellow-50 p-2 rounded-lg shrink-0">
                      <PackageCheck size={18} className="text-yellow-600" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-slate-900 break-all">
                        #{order.orderNumber}
                      </h3>

                      <p className="text-xs text-slate-600 line-clamp-2 break-words">
                        {order.customerId?.name ||
                          order.whatsappCustomerId?.productRegistration?.name ||
                          order.whatsappCustomerId?.customerName ||
                          "Unknown Customer"}
                      </p>

                      <p className="text-[11px] text-slate-500">
                        {formatToDDMMYYYY(order?.createdDate)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrder(order);
                        setIsConfirmationModalOpen(true);
                      }}
                      className="flex shrink-0 items-center justify-center text-sm font-bold text-slate-800 hover:text-blue-600"
                    >
                      <PackageMinus size={18} className="text-red-400" />
                    </button>
                    {role === "admin" && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setIsConfirmationModalDispatchOpen(true);
                        }}
                        className="flex shrink-0 items-center justify-center text-sm font-bold text-slate-800 hover:text-blue-600"
                      >
                        <CircleCheck size={18} className="text-green-700" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <motion.div layout className="space-y-1.5 overflow-hidden">
                    {items.slice(0, 5).map((item: any, idx: number) => (
                      <div
                        key={`${order._id}-${idx}`}
                        className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                      >
                        <span className="font-medium text-gray-800 truncate pr-2">
                          {item.product?.productName ||
                            item.rawMaterial?.name ||
                            "Unknown"}
                        </span>
                        <span className="text-orange-600 font-bold shrink-0">
                          × {item.quantity} {item.unit || ""}
                        </span>
                      </div>
                    ))}

                    <AnimatePresence initial={false}>
                      {limit > 5 && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="space-y-1.5 overflow-hidden mt-1.5"
                        >
                          {items
                            .slice(5, limit)
                            .map((item: any, idx: number) => (
                              <div
                                key={`${order._id}-${idx + 5}`}
                                className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                              >
                                <span className="font-medium text-gray-800 truncate pr-2">
                                  {item.product?.productName ||
                                    item.rawMaterial?.name ||
                                    "Unknown"}
                                </span>
                                <span className="text-orange-600 font-bold shrink-0">
                                  × {item.quantity} {item.unit || ""}
                                </span>
                              </div>
                            ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {items.length > limit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisibleProductsMap((prev) => ({
                          ...prev,
                          [order._id]: limit + 5,
                        }));
                      }}
                      className="w-full py-1.5 text-[10px] text-yellow-700 font-bold bg-yellow-50 hover:bg-yellow-100 rounded-md transition-colors mt-2"
                    >
                      Show {Math.min(5, items.length - limit)} more
                    </button>
                  )}
                </div>

                <div className="mt-auto space-y-2">
                  <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setChallanModalOpen(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 py-2 bg-slate-500 text-white rounded-xl text-[10px] font-bold hover:bg-slate-600 transition-all shadow-sm active:scale-95"
                    >
                      <FileText size={12} />
                      Print Challan
                    </button>
                  </div>

                  <Button
                    type={ButtonType.Submit}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDispatchClick?.(order._id);
                    }}
                    className="w-full text-xs flex items-center justify-center gap-1.5 py-2.5"
                  >
                    <Send size={14} />
                    {totalDispatched > 0
                      ? "Continue Dispatch"
                      : "Start Dispatch"}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {pagination && pagination.total > 0 && (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <PaginationControls
            currentPage={page}
            totalPages={pagination.pages}
            onPageChange={setPage}
            isLoading={loading}
            totalItems={pagination.total}
          />
        </div>
      )}

      <ChallanPreviewModal
        order={selectedOrder}
        isOpen={challanModalOpen}
        onClose={() => {
          setChallanModalOpen(false);
          setSelectedOrder(null);
        }}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => {
          setIsConfirmationModalOpen(false);
          setSelectedOrder(null);
        }}
        onConfirm={async () => {
          const selectedOrderId = selectedOrder?._id;
          if (selectedOrderId) {
            const res = await dispatch(
              removeAssignedWarehouse(selectedOrderId),
            );
            if (res.payload.success) {
              setIsConfirmationModalOpen(false);
              setSelectedOrder(null);
            }
            await fetchPendingDispatchCallback();
          } else {
            toast.error("OrderId not selected");
          }
        }}
        title={`#${selectedOrder?.orderNumber}`}
        subTitle={`Remove Assignment for ${
          selectedOrder?.customerId?.name ||
          selectedOrder?.whatsappCustomerId?.productRegistration?.name ||
          selectedOrder?.whatsappCustomerId?.customerName ||
          "Unknown Customer"
        }?`}
        description="This will reset warehouse assignment and pending dispatch details."
        confirmText="Yes, Remove"
        cancelText="Cancel"
        loading={loading}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalDispatchOpen}
        onClose={() => {
          setIsConfirmationModalDispatchOpen(false);
          setSelectedOrder(null);
        }}
        onConfirm={async () => {
          const selectedOrderId = selectedOrder?._id;
          if (selectedOrderId) {
            const res = await dispatch(moveOrderToCompleted(selectedOrderId));
            if (res.payload?.success) {
              setIsConfirmationModalDispatchOpen(false);
              setSelectedOrder(null);
            }
            await fetchPendingDispatchCallback();
          } else {
            toast.error("OrderId not selected");
          }
        }}
        title={`#${selectedOrder?.orderNumber}`}
        subTitle={`Move Order for ${
          selectedOrder?.customerId?.name ||
          selectedOrder?.whatsappCustomerId?.productRegistration?.name ||
          selectedOrder?.whatsappCustomerId?.customerName ||
          "Unknown Customer"
        } to completed dispatch?`}
        description="This will move the order to completed dispatch and remove it from pending dispatch without updating stock."
        confirmText="Yes, Move to Completed"
        cancelText="Cancel"
        loading={loading}
      />
    </section>
  );
};

export default PendingDispatchTab;
